import {
  BookOpen,
  ClipboardList,
  Brain,
  FileText,
  Heart,
  Layers,
  Route,
  Sunrise,
  TrendingUp,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Single source of truth for the /resurser/artiklar overview.
 *
 * Every article that has a route under `routes.articles.*` (except the
 * `whitepapers` hub) MUST have an entry here, so the overview can never silently
 * omit a published article again. A contract test enforces this 1:1 mapping.
 *
 * Translatable fields carry both the i18n key and a Swedish fallback; the
 * overview resolves them with `t(key, fallback)`.
 */
export interface ArticleI18n {
  key: string;
  fallback: string;
}

export interface ArticleMeta {
  /** routes.ts key under `articles.*`, used to derive the localized URL. */
  routeKey: string;
  /** Product news is a stable subset of the existing article registry. */
  kind?: "news";
  icon: LucideIcon;
  readingTime: string;
  /** ISO date (YYYY-MM-DD); the overview sorts by this, newest first. */
  publishDate: string;
  title: ArticleI18n;
  description: ArticleI18n;
  category: ArticleI18n;
  tags: ArticleI18n[];
}

const category = {
  analysis: { key: "resources:articles.category.analysis", fallback: "Analys" },
  trends: { key: "resources:articles.category.trends", fallback: "Trender" },
  research: {
    key: "resources:articles.category.research",
    fallback: "Forskning",
  },
  news: { key: "resources:articles.category.news", fallback: "Nyheter" },
  opinion: { key: "resources:articles.category.opinion", fallback: "Debatt" },
} as const;

const tag = {
  ai: { key: "resources:articles.tags.ai", fallback: "AI" },
  scheduling: {
    key: "resources:articles.tags.scheduling",
    fallback: "Schemaläggning",
  },
  riskManagement: {
    key: "resources:articles.tags.risk_management",
    fallback: "Riskhantering",
  },
  implementation: {
    key: "resources:articles.tags.implementation",
    fallback: "Implementering",
  },
  future: { key: "resources:articles.tags.future", fallback: "Framtid" },
  trends: { key: "resources:articles.tags.trends", fallback: "Trender" },
  digitalization: {
    key: "resources:articles.tags.digitalization",
    fallback: "Digitalisering",
  },
  optimization: {
    key: "resources:articles.tags.optimization",
    fallback: "Optimering",
  },
  continuity: {
    key: "resources:articles.tags.continuity",
    fallback: "Kontinuitet",
  },
  algorithms: {
    key: "resources:articles.tags.algorithms",
    fallback: "Algoritmer",
  },
  efficiency: {
    key: "resources:articles.tags.efficiency",
    fallback: "Effektivitet",
  },
  research: { key: "resources:articles.tags.research", fallback: "Forskning" },
  founderStory: {
    key: "resources:articles.tags.founder_story",
    fallback: "Grundarberättelse",
  },
  mission: { key: "resources:articles.tags.mission", fallback: "Mission" },
} as const;

export const ARTICLES: ArticleMeta[] = [
  {
    routeKey: "articles.dayInHomeCare2026",
    icon: Sunrise,
    readingTime: "11 min",
    publishDate: "2026-09-27",
    title: {
      key: "resources:articles.day_in_home_care_2026.title",
      fallback: "En dag i hemtjänsten 2026",
    },
    description: {
      key: "resources:articles.day_in_home_care_2026.description",
      fallback:
        "Alla gör sitt bästa och ändå går dagen sönder. En vanlig dag i hemtjänsten, timme för timme, med källor.",
    },
    category: category.opinion,
    tags: [tag.continuity, tag.scheduling, tag.research],
  },
  {
    routeKey: "articles.socialDocumentationHomeCare",
    icon: FileText,
    readingTime: "6 min",
    publishDate: "2026-10-20",
    title: {
      key: "resources:articles.social_documentation_home_care.title",
      fallback: "Social dokumentation i hemtjänsten: Journal enligt SoL",
    },
    description: {
      key: "resources:articles.social_documentation_home_care.description",
      fallback:
        "Vad som ska dokumenteras, en lathund för journalanteckningar och hur Journal i Caire fungerar.",
    },
    category: category.analysis,
    tags: [tag.implementation, tag.digitalization],
  },
  {
    routeKey: "articles.ibicHomeCare",
    icon: ClipboardList,
    readingTime: "5 min",
    publishDate: "2026-10-20",
    title: {
      key: "resources:articles.ibic_home_care.title",
      fallback: "IBIC i hemtjänsten: behov, mål och genomförandeplan",
    },
    description: {
      key: "resources:articles.ibic_home_care.description",
      fallback:
        "Vad IBIC är, hur behov och mål beskrivs i livsområden och hur beslutet blir en genomförandeplan.",
    },
    category: category.analysis,
    tags: [tag.scheduling, tag.implementation],
  },
  {
    routeKey: "articles.employeeHandbookHomeCare",
    icon: BookOpen,
    readingTime: "5 min",
    publishDate: "2026-10-20",
    title: {
      key: "resources:articles.employee_handbook_home_care.title",
      fallback: "Personalhandbok för hemtjänst: vad den ska innehålla",
    },
    description: {
      key: "resources:articles.employee_handbook_home_care.description",
      fallback:
        "En mall för personalhandboken i hemtjänsten: introduktion, rutiner, delegering, avvikelser, sekretess och säkerhet.",
    },
    category: category.analysis,
    tags: [tag.implementation, tag.digitalization],
  },
  {
    routeKey: "articles.planningSystemCostHomeCare",
    icon: Wallet,
    readingTime: "6 min",
    publishDate: "2026-09-25",
    title: {
      key: "resources:articles.planning_system_cost_home_care.title",
      fallback: "Vad kostar ett planeringssystem för hemtjänst?",
    },
    description: {
      key: "resources:articles.planning_system_cost_home_care.description",
      fallback:
        "Så prissätts planeringssystem för hemtjänst, vad som påverkar totalkostnaden och vad Caire kostar.",
    },
    category: category.analysis,
    tags: [tag.implementation, tag.efficiency],
  },
  {
    routeKey: "articles.saivoInvestment",
    kind: "news",
    icon: Heart,
    readingTime: "2 min",
    publishDate: "2026-11-03",
    title: {
      key: "resources:news.saivo.title",
      fallback:
        "Teknikentreprenören bakom Caire vill ge AI en mänsklig uppgift i hemtjänsten",
    },
    description: {
      key: "resources:news.saivo.summary",
      fallback:
        "Teknikentreprenören Björn Evers och Sverker Carlsson förenar bolagsbyggande, AI och hemtjänsterfarenhet i Caire. Nu investerar Saivo Capital inför lanseringen.",
    },
    category: category.news,
    tags: [tag.mission, tag.ai, tag.scheduling],
  },
  {
    routeKey: "articles.freeScheduleAnalysis",
    kind: "news",
    icon: FileText,
    readingTime: "5 min",
    publishDate: "2026-09-14",
    title: {
      key: "resources:news.free_analysis.title",
      fallback: "Analysera er besöksplanering kostnadsfritt – så fungerar det.",
    },
    description: {
      key: "resources:news.free_analysis.summary",
      fallback:
        "Så fungerar Caires kostnadsfria schemaanalys av en vald period.",
    },
    category: category.news,
    tags: [tag.scheduling, tag.optimization, tag.efficiency],
  },
  {
    routeKey: "articles.aiAssistantMcp",
    kind: "news",
    icon: Brain,
    readingTime: "4 min",
    publishDate: "2026-09-14",
    title: {
      key: "resources:news.mcp.title",
      fallback: "Koppla en AI-assistent till Caire med MCP.",
    },
    description: {
      key: "resources:news.mcp.summary",
      fallback:
        "Koppla kompatibla AI-verktyg till tillgänglig planeringsdata och förbered granskningsbara förslag.",
    },
    category: category.news,
    tags: [tag.ai, tag.implementation, tag.scheduling],
  },
  {
    routeKey: "articles.aiSchedulingHomeCare",
    icon: Brain,
    readingTime: "8 min",
    publishDate: "2026-08-03",
    title: {
      key: "resources:articles.ai_scheduling_home_care.title",
      fallback:
        "AI-schemaläggning i hemtjänsten: en praktisk guide för samordnare",
    },
    description: {
      key: "resources:articles.ai_scheduling_home_care.description",
      fallback:
        "Vad innebär AI-schemaläggning i hemtjänsten? En praktisk guide till indata, mänsklig granskning, uppföljning och införande.",
    },
    category: category.analysis,
    tags: [tag.ai, tag.scheduling, tag.implementation],
  },
  {
    routeKey: "articles.reduceTravelTimeHomeCare",
    icon: Route,
    readingTime: "7 min",
    publishDate: "2026-08-03",
    title: {
      key: "resources:articles.reduce_travel_time_home_care.title",
      fallback: "Minska restiden i hemtjänsten utan att tappa kontinuitet",
    },
    description: {
      key: "resources:articles.reduce_travel_time_home_care.description",
      fallback:
        "Praktiska sätt att minska restid i hemtjänsten: geografisk planering, rätt mått, kontinuitet och uppföljning av rutter.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.continuity, tag.efficiency],
  },
  {
    routeKey: "articles.staffPlanningHomeCare",
    icon: TrendingUp,
    readingTime: "8 min",
    publishDate: "2026-08-03",
    title: {
      key: "resources:articles.staff_planning_home_care.title",
      fallback:
        "Personalplanering i hemtjänsten: från bemanningsgap till bättre brukartid",
    },
    description: {
      key: "resources:articles.staff_planning_home_care.description",
      fallback:
        "En praktisk modell för personalplanering i hemtjänsten: kapacitet, efterfrågan, kompetens, skift och uppföljning.",
    },
    category: category.analysis,
    tags: [tag.scheduling, tag.efficiency, tag.optimization],
  },
  {
    routeKey: "articles.carefoxAiScheduling",
    icon: Layers,
    readingTime: "7 min",
    publishDate: "2026-08-03",
    title: {
      key: "resources:articles.carefox_ai_scheduling.title",
      fallback:
        "Verksamhetssystem och AI-schemaläggning: så fungerar ett kompletterande arbetssätt",
    },
    description: {
      key: "resources:articles.carefox_ai_scheduling.description",
      fallback:
        "Så kan hemtjänstverksamheter komplettera sitt verksamhetssystem med AI-stöd för schema, rutter, kontinuitet och granskningsbara förslag.",
    },
    category: category.analysis,
    tags: [tag.ai, tag.scheduling, tag.implementation],
  },
  {
    routeKey: "articles.unassignedVisitsHomeCare",
    icon: Zap,
    readingTime: "7 min",
    publishDate: "2026-08-03",
    title: {
      key: "resources:articles.unassigned_visits_home_care.title",
      fallback:
        "Otilldelade besök i hemtjänsten: orsaker, åtgärder och uppföljning",
    },
    description: {
      key: "resources:articles.unassigned_visits_home_care.description",
      fallback:
        "Så analyserar ni otilldelade besök i hemtjänsten och skiljer på kapacitetsbrist, villkor, tidsfönster och bristande indata.",
    },
    category: category.analysis,
    tags: [tag.scheduling, tag.optimization, tag.efficiency],
  },
  {
    routeKey: "articles.scheduleOptimizationCareTimeTravelTime",
    icon: Route,
    readingTime: "9 min",
    publishDate: "2026-07-25",
    title: {
      key: "resources:articles.schemaoptimering_brukartid_restid.title",
      fallback:
        "Schemaoptimering för planerare: så hänger brukartid och restid ihop",
    },
    description: {
      key: "resources:articles.schemaoptimering_brukartid_restid.description",
      fallback:
        "Brukartid och restid styrs av samma planeringsvariabler. Tre hävstänger för att sänka restid och höja brukartid samtidigt, utan att pressa personalen.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.scheduling, tag.efficiency],
  },
  {
    routeKey: "articles.missionAfterMs",
    icon: Heart,
    readingTime: "9 min",
    publishDate: "2026-07-15",
    title: {
      key: "resources:articles.mission_after_ms.title",
      fallback: "När kroppen säger nej, men huvudet säger ja",
    },
    description: {
      key: "resources:articles.mission_after_ms.description",
      fallback:
        "Björn Evers diagnostiserades med MS vid 35. Efter 5 000+ hemtjänstbesök grundade han Caire — omsorg först, AI som möjliggörare.",
    },
    category: {
      key: "resources:articles.category.founder_story",
      fallback: "Grundarberättelse",
    },
    tags: [tag.founderStory, tag.mission, tag.ai],
  },
  {
    routeKey: "articles.routingScienceHomeCare",
    icon: Route,
    readingTime: "18 min",
    publishDate: "2026-06-30",
    title: {
      key: "resources:articles.routing_science.title",
      fallback: "Routing science i hemtjänst: avväg restid och kontinuitet",
    },
    description: {
      key: "resources:articles.routing_science.description",
      fallback:
        "Hur modern ruttoptimering väger restid, kontinuitet, kompetens, tidsfönster och mänsklig granskning i ett sammanhållet schema.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.continuity, tag.scheduling, tag.research],
  },
  {
    routeKey: "articles.efficiency80Hemtjanst",
    icon: TrendingUp,
    readingTime: "10 min",
    publishDate: "2026-03-27",
    title: {
      key: "resources:articles.efficiency_80_hemtjanst.title",
      fallback: "Brukartid och kontinuitet i hemtjänsten",
    },
    description: {
      key: "resources:articles.efficiency_80_hemtjanst.description",
      fallback:
        "Mål, KPI-definitioner och räkneexempel för att följa brukartid, personalkontinuitet och arbetsmiljö tillsammans.",
    },
    category: category.research,
    tags: [tag.efficiency, tag.continuity, tag.scheduling, tag.research],
  },
  {
    routeKey: "articles.constraintManagementHomeCareScheduling",
    icon: Layers,
    readingTime: "16 min",
    publishDate: "2026-03-25",
    title: {
      key: "resources:articles.constraint_management.title",
      fallback: "Effektiv villkorshantering vid schemaoptimering för hemtjänst",
    },
    description: {
      key: "resources:articles.constraint_management.description",
      fallback:
        "Hur man balanserar hårda och mjuka villkor i hemtjänstschemaläggning. Caires tre-lagermodell, KISS-principen och strategier som hanterar 85 % av alla villkor med tidsfönster.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.scheduling, tag.algorithms, tag.ai],
  },
  {
    routeKey: "articles.kontinuitetEffektivitetAI",
    icon: Zap,
    readingTime: "12 min",
    publishDate: "2026-03-12",
    title: {
      key: "resources:articles.continuity_efficiency_ai.title",
      fallback: "Kontinuitet och Effektivitet i Hemtjänsten: AI-Optimering",
    },
    description: {
      key: "resources:articles.continuity_efficiency_ai.description",
      fallback:
        "Forskningsartikel om AI-optimering av hemtjänst. En verklig tvåveckorsplanering i Stockholmsområdet gav 5,67 unika vårdgivare per brukare och 79,1 % brukartid.",
    },
    category: category.research,
    tags: [tag.ai, tag.continuity, tag.efficiency, tag.research],
  },
  {
    routeKey: "articles.balancingEfficiencyContinuity",
    icon: Layers,
    readingTime: "13 min",
    publishDate: "2026-03-04",
    title: {
      key: "resources:articles.balancing_efficiency.title",
      fallback: "Balansera effektivitet och kontinuitet i hemtjänsten",
    },
    description: {
      key: "resources:articles.balancing_efficiency.description",
      fallback:
        "Bättre planering handlar inte om att välja mellan kortare restid och bättre kontinuitet. Rätt modell gör båda målen synliga, konfigurerbara och praktiskt användbara.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.continuity, tag.efficiency],
  },
  {
    routeKey: "articles.optimizationContinuityAlgorithms",
    icon: FileText,
    readingTime: "14 min",
    publishDate: "2026-03-02",
    title: {
      key: "resources:articles.optimization_continuity.title",
      fallback: "Optimering och kontinuitet: algoritmer för hemtjänsten",
    },
    description: {
      key: "resources:articles.optimization_continuity.description",
      fallback:
        "Så använder vi matematisk optimering och algoritmer för effektiv visitplanering, personal kontinuitet och rättvis fördelning.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.continuity, tag.algorithms, tag.efficiency],
  },
  {
    routeKey: "articles.vrptwHomeCareOptimization",
    icon: FileText,
    readingTime: "18 min",
    publishDate: "2026-03-02",
    title: {
      key: "resources:articles.vrptw_home_care.title",
      fallback:
        "VRPTW hemtjänstoptimering: besökstäckning och restidsavvägningar",
    },
    description: {
      key: "resources:articles.vrptw_home_care.description",
      fallback:
        "Hur CAIRE använder VRPTW-algoritmer och hemtjänstspecifika begränsningar för att jämföra besökstäckning och restidsavvägningar i optimeringskörningarna.",
    },
    category: category.analysis,
    tags: [tag.optimization, tag.algorithms, tag.scheduling, tag.efficiency],
  },
  {
    routeKey: "articles.comprehensiveAiInHomeCare",
    icon: Brain,
    readingTime: "15 min",
    publishDate: "2025-05-25",
    title: {
      key: "resources:articles.ai_revolution.title",
      fallback: "Den kompletta guiden till AI i hemtjänsten",
    },
    description: {
      key: "resources:articles.ai_revolution.description",
      fallback:
        "En djupgående utforskning av Artificiell Intelligens inom hemtjänsten, som täcker den revolutionerande potentialen, inneboende risker och praktiska tillämpningar.",
    },
    category: category.analysis,
    tags: [tag.ai, tag.scheduling, tag.riskManagement, tag.implementation],
  },
  {
    routeKey: "articles.futureHomeCare",
    icon: TrendingUp,
    readingTime: "8 min",
    publishDate: "2024-02-20",
    title: {
      key: "resources:articles.future.title",
      fallback: "Framtidens hemtjänsttrender 2030",
    },
    description: {
      key: "resources:articles.future.description",
      fallback:
        "En djupdykning i de teknologiska och organisatoriska trender som kommer forma hemtjänsten de kommande åren.",
    },
    category: category.trends,
    tags: [tag.future, tag.trends, tag.digitalization],
  },
];

/** The product-news hub and homepage preview share this registry-backed view. */
export const NEWS_ARTICLES = ARTICLES.filter(
  (article) => article.kind === "news",
);
